/**
 * @file 『2才児のさんすう』(伊藤恭・ピグマリオン学育研究所) の進め方カタログ。
 *
 * @what: 本の 7 章を「順に進む 7 つの段」として持ち、段ごとのねらい・やること (遊びと教具)・
 *   本のページ・時期の目安を並べる。見取り図タブの「さんすうの進め方」カードが読む。
 * @why: 本は解説と教具の型紙が交互に並ぶ作りで、通して読まないと「いつ何をやるか」が
 *   掴めない (代表 2026-08-25)。順序と現在地だけを取り出して、開くページに直行できるようにする。
 *   ページ番号は紙に刷られた番号で持つ。本だなの資料 `sansuu-2sai` の目次とページはリーダーの
 *   連番なので、開く先は資料の reader_offset を足して決める (model/sourcePages.ts)。
 * @failure: 描画専用の定数。本だなに資料が無い環境ではページのリンクを出さない (文言は出る)。
 *   本文の要約であって引用ではない (原文は本だなの該当ページで読む)。
 */

/** 本だなに入れた資料 id。ページのリンクはこの資料の該当ページを開く。 */
export const SANSUU_SOURCE_ID = "sansuu-2sai";

export interface SansuuStep {
  id: string;
  /** 本の章 (第 I 章 など)。 */
  chapter: string;
  /** 段の名前 (画面の見出し)。 */
  label: string;
  /** 章の開始ページ (紙の印刷ページ)。 */
  page: number;
  /** その段でできるようになること。 */
  goal: string;
  /** 時期の目安 (本文に書かれているものだけ。無い段は空)。 */
  timing?: string;
  /** やること (遊び・教具) と、その解説が始まるページ。 */
  activities: readonly { label: string; page?: number }[];
}

/** 指導に入る前の心得 (本の「指導の前に、読んでください」p.2〜4 の要点)。 */
export const SANSUU_PRINCIPLES: readonly { label: string; note: string; page: number }[] = [
  {
    label: "教えない",
    note: "指図すると、子どもが考える前に親が考えてしまい、やる気が下がると説きます。答えを言わずに、遊びの中で気づくのを待ちます",
    page: 2,
  },
  {
    label: "指先を動かす",
    note: "指先は「第 2 の脳」で、数の理解の土台になります。2 歳前後で大豆を箸でつまめるくらいを目安に挙げています",
    page: 3,
  },
  {
    label: "できる・できないで見ない",
    note: "そのままの状態を認めることが先で、能力以上のことを求めないよう促しています",
    page: 4,
  },
];

/**
 * 7 つの段。順に進み、前の段が確実になってから次へ移る。
 * 数える (1, 2, 3…と唱える) のでなく「ぱっと見ていくつか分かる」ことを一貫して目指す本。
 */
export const SANSUU_FLOW: readonly SansuuStep[] = [
  {
    id: "n1_3",
    chapter: "第 I 章",
    label: "1〜3 が、ぱっと見て分かる",
    page: 5,
    goal: "1・2・3 を、数えずに全体として見分けられる。ここが数の力のいちばんの土台になります",
    timing: "2 歳児なら半年かけて、確実に身につくまで遊ぶ",
    activities: [
      { label: "絵数カード", page: 7 },
      { label: "競争ゲーム A 型", page: 21 },
      { label: "ドット棒 A 型", page: 27 },
      { label: "数唱と計数", page: 32 },
      { label: "ウッディブロック", page: 36 },
      { label: "洗濯バサミ遊び", page: 37 },
      { label: "魚釣り", page: 43 },
      { label: "数体験教材", page: 47 },
      { label: "日常生活の中で" },
    ],
  },
  {
    id: "n1_5",
    chapter: "第 II 章",
    label: "1〜5 が、ぱっと見て分かる",
    page: 59,
    goal: "5 までを、数えずに見分けられる。ここまで来ると、たし算・ひき算は自然にできるようになります",
    activities: [
      { label: "絵数カード", page: 61 },
      { label: "競争ゲーム B 型", page: 71 },
      { label: "ドット棒 B 型", page: 76 },
      { label: "親子カード", page: 81 },
      { label: "ウッディブロック", page: 91 },
      { label: "洗濯バサミ遊び", page: 93 },
      { label: "ヌマーカステン", page: 103 },
      { label: "数体験教材", page: 107 },
      { label: "日常生活の中で" },
    ],
  },
  {
    id: "add",
    chapter: "第 III 章",
    label: "1〜5 のたし算",
    page: 115,
    goal: "指を折って数えるのでなく、数を合わせる感覚で足せる",
    timing: "2 歳半から始めるなら、本全体をひととおり進めるのに 1 年を見込む",
    activities: [
      { label: "ドット棒 B 型", page: 116 },
      { label: "競争ゲーム", page: 117 },
      { label: "左右の手で合成", page: 118 },
      { label: "ヌマーカステン", page: 119 },
      { label: "親子カード", page: 121 },
      { label: "トランプ遊び", page: 122 },
    ],
  },
  {
    id: "sub",
    chapter: "第 IV 章",
    label: "1〜5 のひき算",
    page: 123,
    goal: "合わせた数は元に分けられる、という感覚を、たし算と同時につかむ",
    timing: "たし算と切り離さず、同じ時期に一緒に経験させる",
    activities: [
      { label: "ドット棒 B 型", page: 124 },
      { label: "競争ゲーム", page: 125 },
      { label: "左右の手で分解", page: 126 },
      { label: "ヌマーカステン", page: 127 },
      { label: "親子カード", page: 129 },
      { label: "トランプ遊び", page: 130 },
    ],
  },
  {
    id: "check",
    chapter: "第 V 章",
    label: "紙で確かめる",
    page: 131,
    goal: "数字を書けなくても答えられる形で、ここまでの力を見る",
    activities: [
      { label: "同数発見・異数発見", page: 131 },
      { label: "2 つ多い数・合わせた数・たりない数", page: 131 },
    ],
  },
  {
    id: "n1_12",
    chapter: "第 VI 章",
    label: "1〜12 に広げる",
    page: 139,
    goal: "10 を超える数へ。時計や 1 ダースなど、12 でまとまる数にも触れます",
    activities: [
      { label: "絵数カード", page: 140 },
      { label: "ドット棒 C 型", page: 151 },
      { label: "ウッディブロック", page: 157 },
      { label: "数体験教材", page: 161 },
      { label: "ヌマーカステン", page: 179 },
      { label: "同数・異数発見", page: 183 },
    ],
  },
  {
    id: "print",
    chapter: "第 VII 章",
    label: "プリントに挑戦",
    page: 191,
    goal: "頭に思い浮かべて考える。ここは急がなくてよい段です",
    timing: "1〜12 の数を扱えるようになってからで間に合う",
    activities: [
      { label: "計算問題", page: 191 },
      { label: "文章問題", page: 191 },
    ],
  },
];
